import { z } from "zod";
import { InternalServerError } from "../errors/base.error.js";
import {
  EventIdPublicSchema,
  EventParticipantIdPublicSchema,
  UserIdPublicSchema,
  AnyIdSchema,
  anyIdCompanion,
  type AnyId,
} from "./branded-id.schemas.js";
import {
  UserAppRoutes,
  type EventRouteContext,
} from "../constants/user-app-routes.constants.js";

// Values accept branded ids (a query param is often an id); they are unwrapped
// to plain strings only here, at the URL-materialization boundary.
const extraQueryParams = z.record(z.string(), AnyIdSchema).optional();
const eventFields = {
  eventIdPublic: EventIdPublicSchema,
  extraQueryParams,
};

const ForEventRouteSchema = z.object({
  type: z.literal("forEvent"),
  ...eventFields,
  route: z.enum([
    "directory",
    "directoryAll",
    "directoryTopPicks",
    "directoryRequests",
    "directoryConnected",
    "directorySent",
    "profileEdit",
    "profileEditPreferences",
  ]),
});

const ForEventParticipantRouteSchema = z.object({
  type: z.literal("forEventParticipant"),
  ...eventFields,
  participantIdPublic: EventParticipantIdPublicSchema,
  route: z.enum(["participantProfile", "participantProfileWhyConnect"]),
});

const EventLandingPageRouteSchema = z.object({
  type: z.literal("eventLandingPage"),
  ...eventFields,
});

const UserAccountRouteSchema = z.object({
  type: z.literal("userAccount"),
  userIdPublic: UserIdPublicSchema,
  route: z.enum(["userAccount", "userAccountEvents"]),
  extraQueryParams,
});

const StaticRouteSchema = z.object({
  type: z.literal("static"),
  route: z.enum(["auth", "terms", "privacy"]),
  extraQueryParams,
});

export const UserRouteRequestSchema = z.discriminatedUnion("type", [
  ForEventRouteSchema,
  ForEventParticipantRouteSchema,
  EventLandingPageRouteSchema,
  UserAccountRouteSchema,
  StaticRouteSchema,
]);

export type UserRouteRequest = z.infer<typeof UserRouteRequestSchema>;

export const USER_ROUTE_PROTOCOL = "user-route://";

const UserRouteErrorMessages = {
  INVALID_PROTOCOL: `URL does not start with ${USER_ROUTE_PROTOCOL}`,
  DECODE_FAILED: "Failed to decode user-route URL",
  MISSING_EVENT_CONTEXT:
    "EventRouteContext is required for event-scoped routes",
} as const;

export class UserRouteDecodingError extends InternalServerError {
  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- an error cause is genuinely unknown (may originate outside our code)
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
  }
}

export class UserRouteResolutionError extends InternalServerError {
  constructor(message: string) {
    super(message);
  }
}

export function isUserRouteUrl(url: string): boolean {
  return url.startsWith(USER_ROUTE_PROTOCOL);
}

export function encodeUserRoute(request: UserRouteRequest): string {
  return `${USER_ROUTE_PROTOCOL}${encodeURIComponent(toBase64(JSON.stringify(request)))}`;
}

// `btoa` takes one byte per character and throws past U+00FF, so free text in a
// route (a dash, an emoji) is turned into its UTF-8 bytes first.
function toBase64(text: string): string {
  let binary = "";
  for (const byte of new TextEncoder().encode(text)) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function fromBase64(base64: string): string {
  return new TextDecoder().decode(
    Uint8Array.from(atob(base64), (char) => char.charCodeAt(0)),
  );
}

export function decodeUserRoute(url: string): UserRouteRequest {
  if (!url.startsWith(USER_ROUTE_PROTOCOL)) {
    throw new UserRouteDecodingError(UserRouteErrorMessages.INVALID_PROTOCOL);
  }
  try {
    const raw: unknown = JSON.parse(
      fromBase64(decodeURIComponent(url.slice(USER_ROUTE_PROTOCOL.length))),
    );
    return UserRouteRequestSchema.parse(raw);
  } catch (error) {
    throw new UserRouteDecodingError(
      UserRouteErrorMessages.DECODE_FAILED,
      error,
    );
  }
}

function appendExtraQueryParams(
  path: string,
  params: Record<string, AnyId> | undefined,
): string {
  if (params === undefined || Object.keys(params).length === 0) return path;
  const unwrapped: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    unwrapped[key] = anyIdCompanion.unwrap(value);
  }
  const qs = new URLSearchParams(unwrapped).toString();
  return path.includes("?") ? `${path}&${qs}` : `${path}?${qs}`;
}

function requireEventContext(
  ctx: EventRouteContext | undefined,
): EventRouteContext {
  if (ctx === undefined) {
    throw new UserRouteResolutionError(
      UserRouteErrorMessages.MISSING_EVENT_CONTEXT,
    );
  }
  return ctx;
}

function resolveBasePath(
  request: UserRouteRequest,
  ctx: EventRouteContext | undefined,
): string {
  switch (request.type) {
    case "forEvent":
      return UserAppRoutes.forEvent(requireEventContext(ctx))[request.route]();
    case "forEventParticipant":
      return UserAppRoutes.forEvent(requireEventContext(ctx))[request.route](
        request.participantIdPublic,
      );
    case "eventLandingPage":
      return UserAppRoutes.eventLandingPage(requireEventContext(ctx));
    case "userAccount":
      return UserAppRoutes[request.route](request.userIdPublic);
    case "static":
      return UserAppRoutes[request.route]();
  }
}

export function resolveUserRoute(
  request: UserRouteRequest,
  ctx?: EventRouteContext,
): string {
  const basePath = resolveBasePath(request, ctx);
  return appendExtraQueryParams(basePath, request.extraQueryParams);
}
